//dependencies
import { arrayMove } from "@dnd-kit/sortable";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { faPlus } from "@fortawesome/pro-solid-svg-icons";

import { toast } from "@performyard/py-components";

//components
import IconButton from "../../../../../components/Button/IconButton";
import DragAndDrop from "../../../../../components/DragAndDrop";
import Spacer from "../../../../../components/Spacer";
import Typography from "../../../../../components/Text";
import ChecklistItem, { ChecklistItem as ListItem } from "./components/ChecklistItem";

//hooks
import {
	useGoalDetails,
	useGoalHistory,
	useMutateAddGoalChecklistItems,
	useMutateDeleteGoalChecklistItems,
	useMutateGoalDetails,
	useMutateUpdateGoalChecklistItems,
	useRbacObjectPermissions,
} from "../../../../../api/hooks";
import useSearchQueryParams from "../../../../../hooks/useSearchQueryParams";

import { Goal } from "../../../../../api/services";

import { parseGoalPermissionsForUser } from "../../utils";

//config
import Config from "../../config";
//css
import css from "./Checklist.module.scss";

const Checklist = () => {
	const { handleQueryChange } = useSearchQueryParams({
		schema: Config.queryParametersSchema,
	});

	const [animationParent, enableAnimation] = useAutoAnimate();
	const [animationParentMainList, enableAnimationMainList] = useAutoAnimate();
	const { goal_id: goalId = "" } = useParams<{ goal_id: string }>();

	const { data, refetch: refetchGoal, isRefetching: isGoalRefetching } = useGoalDetails({ goalID: goalId || "" });
	const goal = data?.goal || ({} as Goal);
	const { data: permissions } = useRbacObjectPermissions("goal", goalId);

	const { refetch: refetchGoalHistory } = useGoalHistory({
		goalID: goalId,
		sortDirection: "desc",
		sortName: "_id",
	});

	const { refetch: refetchProgress } = useGoalHistory({
		goalID: goalId,
		type: "progress",
		sortDirection: "desc",
		sortName: "as_of",
	});

	const { userCanReorderChecklists, userCanAddChecklistItem, userCanEditChecklistItemStatus } =
		parseGoalPermissionsForUser(permissions);

	const [showAddItemInput, setShowAddItemInput] = useState<boolean>(false);
	const [goalChecklist, setGoalChecklist] = useState<ListItem[]>(goal?.checklist || []);
	const [cachedChecklist, setCachedChecklist] = useState<ListItem[]>(goal?.checklist || []);
	const [pendingList, setPendingList] = useState<ListItem[]>([]);

	const { mutate: updateChecklist } = useMutateGoalDetails(goal?._id, {
		onSuccess: () => {
			refetchGoal();
			refetchGoalHistory();
			refetchProgress();
		},
		onError: () => {
			toast.error("Something went wrong, please try again");
			setGoalChecklist(cachedChecklist);
		},
	});

	const { mutate: addChecklist, isPending: isAddPending } = useMutateAddGoalChecklistItems(
		goal?._id,
		() => {
			refetchGoalHistory();
			refetchProgress();
		},
		() => {
			toast.error("Something went wrong, please try again");
			setGoalChecklist(cachedChecklist);
		}
	);

	const { mutate: deleteChecklist } = useMutateDeleteGoalChecklistItems(
		goal?._id,
		() => {
			refetchGoalHistory();
			refetchProgress();
		},
		() => {
			toast.error("Something went wrong, please try again");
			setGoalChecklist(cachedChecklist);
		}
	);

	const { mutate: updateChecklistItems } = useMutateUpdateGoalChecklistItems(
		goal?._id,
		() => {
			refetchGoalHistory();
			refetchProgress();
		},
		() => {
			toast.error("Something went wrong, please try again");
			setGoalChecklist(cachedChecklist);
		}
	);

	useEffect(() => {
		if (!isAddPending) {
			refetchGoal();
		}
	}, [isAddPending]);

	useEffect(() => {
		if (!isGoalRefetching && !isAddPending) {
			setGoalChecklist(goal?.checklist || []);
			setCachedChecklist(goal?.checklist || []);
			setPendingList([]);
		}
	}, [isGoalRefetching]);

	const handleAddChecklistItem = (item: ListItem) => {
		enableAnimation(true);
		enableAnimationMainList(false);

		// only allow adding if the item has text
		if (item?.text !== "") {
			enableAnimation(false);

			// add goal
			setPendingList([item, ...pendingList]);
			addChecklist([item]);
		}
	};

	const handleRemoveChecklistItem = (itemToRemoveID: string) => {
		enableAnimationMainList(true);
		enableAnimation(false);

		// remove goal
		const newChecklist = goalChecklist.filter((item) => item._id !== itemToRemoveID);
		deleteChecklist([itemToRemoveID]);
		setCachedChecklist(goalChecklist);
		setGoalChecklist(newChecklist);
	};

	const handleOnDragEnd = (event) => {
		const { active, over } = event;
		const items = [...goalChecklist];
		if (active.id !== over.id) {
			const activeIndex = items.findIndex((item) => item._id === active.id);
			const overIndex = items.findIndex((item) => item._id === over.id);
			let orderedItems = arrayMove(items, activeIndex, overIndex);

			//remove any .id
			orderedItems = orderedItems.map((item) => {
				const { id, ...rest } = item;
				return rest;
			});

			updateChecklist([{ field: "checklist", value: orderedItems }]);
			setGoalChecklist(orderedItems);
			setCachedChecklist(goalChecklist);
		}
	};

	const handleOpenProgressModalOnChecklistClick = (newList: ListItem[] = goalChecklist) => {
		if (goal?.checklist_updates_goal_progress && goalChecklist.length > 0) {
			const numberOfCheckedItems = newList.filter((item) => item.checked).length;
			const initialValue = (numberOfCheckedItems / newList.length) * goal?.target;
			handleQueryChange({ modal: "updateProgress", initialGoalProgressValue: initialValue });
		}
	};

	const handleOnEditChecklistItem = (item: ListItem) => {
		enableAnimation(false);

		// update goal
		const index = goalChecklist.findIndex((listItem) => listItem._id === item._id);
		const original = goalChecklist[index];
		const newList = [...goalChecklist];
		newList[index] = item;
		delete item.id;
		updateChecklistItems([item]);
		setGoalChecklist(newList);
		setCachedChecklist(goalChecklist);

		//if the item has its check changed, open the progress modal
		if (item.checked !== original.checked) {
			handleOpenProgressModalOnChecklistClick(newList);
		}
	};

	return (
		<div data-testid="checklistContainer">
			<div className={css.header}>
				<Typography size="paragraph-2" weight="semi-bold">
					Goal Checklist
				</Typography>
				{userCanAddChecklistItem && (
					<IconButton
						isHollow
						square
						icon={faPlus}
						onClick={() => {
							setShowAddItemInput(true);
						}}
						disabled={showAddItemInput || !userCanAddChecklistItem}
						testId="add-checklist-item-button"
						aria-label="Add checklist item"
					/>
				)}
			</div>
			<Spacer height={1} />
			<div ref={animationParent}>
				{showAddItemInput && (
					<ChecklistItem
						onChange={(item) => handleAddChecklistItem(item)}
						onDelete={() => {
							setShowAddItemInput(false);
						}}
						item={null}
						overrideId="new-item"
						isCreateNew={true}
						isDeletable={userCanAddChecklistItem}
					/>
				)}
			</div>
			<div>
				{pendingList.map((item, index) => {
					return (
						<div key={`pending-${index}`} id={`pending-${index}`}>
							<ChecklistItem
								onChange={(item) => handleOnEditChecklistItem(item)}
								onDelete={handleRemoveChecklistItem}
								item={item}
								isDraggable={!!(item?._id && userCanReorderChecklists)}
								isLoading={true}
								isDeletable={userCanAddChecklistItem}
							/>
						</div>
					);
				})}
			</div>
			<DragAndDrop
				handleOnDragEnd={handleOnDragEnd}
				itemIds={userCanReorderChecklists ? goalChecklist.map((item) => (item._id ? item._id : "")) : []}
				draggableNodeItems={goalChecklist.map((item) => {
					if (!item?.pending) {
						return (
							<ChecklistItem
								onChange={(item) => handleOnEditChecklistItem(item)}
								onDelete={handleRemoveChecklistItem}
								item={item}
								isDraggable={(item?._id && userCanReorderChecklists) || false}
								isEditable={userCanEditChecklistItemStatus}
								isDeletable={userCanAddChecklistItem}
								key={item._id}
							/>
						);
					} else {
						return null;
					}
				})}
				testId="checklist"
				itemsContainerRef={animationParentMainList}
			/>
		</div>
	);
};

export default Checklist;
